import { useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiPost, getAuthHeaders } from '../../lib/api'
import { 
  ArrowLeft, 
  Upload, 
  X,
  Loader2,
  AlertCircle,
  Calendar,
  Building2,
  Award,
  FileText
} from 'lucide-react'

interface CertificationFormData {
  certName?: string
  issuer?: string
  issueDate?: string
  expirationDate?: string
  certNumber?: string
  state?: string
}

export default function AddCertificationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const mode = searchParams.get('mode') || 'scan'
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<'upload' | 'form'>(mode === 'manual' ? 'form' : 'upload')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isExtracting, setIsExtracting] = useState(false)
  const [extractError, setExtractError] = useState('')
  const [uploadedDocumentUrl, setUploadedDocumentUrl] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isPdf, setIsPdf] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  
  const [form, setForm] = useState<CertificationFormData>({
    certName: '',
    issuer: '',
    issueDate: '',
    expirationDate: '',
    certNumber: '',
    state: '',
  })

  const saveMutation = useMutation({
    mutationFn: async (data: CertificationFormData & { documentUrl?: string; source?: string }) => {
      return apiPost<{ certification: { id: string } }>('/certifications', {
        ...data,
        documentUrl: uploadedDocumentUrl || undefined,
        source: data.source || (mode === 'manual' ? 'manual' : 'ai_extracted'),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/certifications'] })
      navigate('/')
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }

  const processFile = (file: File) => {
    setSelectedFile(file)
    setExtractError('')
    setIsPdf(file.type === 'application/pdf')
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file))
    } else {
      setPreviewUrl(null)
    }
    handleExtract(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleExtract = async (file: File) => {
    setIsExtracting(true)
    setExtractError('')

    try {
      const formData = new FormData()
      formData.append('file', file)

      const uploadResponse = await fetch('/api/upload', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload document')
      }

      const uploadData = await uploadResponse.json()
      setUploadedDocumentUrl(uploadData.url || uploadData.fileKey || null)

      const extractResponse = await fetch('/api/extract-certification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ imageUrl: uploadData.url }),
      })

      if (!extractResponse.ok) {
        const errData = await extractResponse.json().catch(() => ({}))
        throw new Error(errData.message || 'Failed to extract data from document')
      }

      const responseData = await extractResponse.json()
      const data = responseData.extractedData || responseData
      
      setForm({
        certName: data.certName || '',
        issuer: data.issuer || '',
        issueDate: data.issueDate || '',
        expirationDate: data.expirationDate || '',
        certNumber: data.certNumber || '',
        state: data.state || '',
      })
      setStep('form')
    } catch (error) {
      setExtractError(error instanceof Error ? error.message : 'Failed to extract data')
    } finally {
      setIsExtracting(false)
    }
  }

  const handleSave = () => {
    if (!form.certName) {
      return
    }
    saveMutation.mutate(form)
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">
          {step === 'upload' ? 'Upload Certification' : 'Add Certification'}
        </h1>
      </div>

      {step === 'upload' ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {!selectedFile ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Upload your certification document
              </p>
              <p className="text-gray-500 mb-4">
                Drag and drop or click to select a file
              </p>
              <p className="text-sm text-gray-400">
                Supports JPG, PNG, PDF up to 10MB
              </p>
            </div>
          ) : extractError ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{extractError}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSelectedFile(null)
                    setExtractError('')
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Try Again
                </button>
                <button
                  onClick={() => setStep('form')}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                >
                  Enter Manually
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              <p className="text-lg font-medium text-gray-900">Reading your certification...</p>
              <p className="text-sm text-gray-500">This may take a few moments</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="space-y-4">
            {(previewUrl || isPdf) && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Uploaded document"
                    className="w-full max-h-48 object-contain"
                  />
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3">
                    <FileText className="w-8 h-8 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFile?.name || 'Document'}</p>
                      <p className="text-xs text-gray-500">PDF document uploaded</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certification Name *
              </label>
              <div className="relative">
                <Award className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={form.certName}
                  onChange={(e) => setForm({ ...form, certName: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., BLS Provider"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issuing Organization
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={form.issuer}
                  onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., American Heart Association"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issue Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={form.issueDate}
                    onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={form.expirationDate}
                    onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Certification Number
                </label>
                <input
                  type="text"
                  value={form.certNumber}
                  onChange={(e) => setForm({ ...form, certNumber: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., TX"
                  maxLength={2}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => mode === 'manual' ? navigate('/') : setStep('upload')}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                {mode === 'manual' ? 'Cancel' : 'Back'}
              </button>
              <button
                onClick={handleSave}
                disabled={!form.certName || saveMutation.isPending}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Certification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
